import { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Headphones, Loader2, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../lib/apiError';

const API = process.env.REACT_APP_BACKEND_URL;

interface Props {
    tenantSlug: string;
    tenantName: string;
    tenantType: 'pharmacy' | 'clinic';
    userName?: string;
    userEmail?: string;
}

export default function FloatingHelpButton({ tenantSlug, tenantName, tenantType, userName, userEmail }: Props) {
    const [open, setOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        contact_name: userName || '',
        contact_email: userEmail || '',
        callback_number: '',
        issue_subject: '',
        issue_description: '',
        priority: 'Medium',
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.contact_name || !form.contact_email || !form.callback_number || !form.issue_subject || !form.issue_description) {
            toast.error('Please fill in all fields');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/api/it/tickets`, {
                tenant_slug: tenantSlug,
                tenant_name: tenantName,
                tenant_type: tenantType,
                ...form,
            });
            setSubmitted(true);
            toast.success('Support request submitted!');
        } catch (err: any) {
            toast.error(getApiErrorMessage(err, 'Failed to submit request'));
        }
        setLoading(false);
    };

    const reset = () => {
        setOpen(false);
        setTimeout(() => {
            setSubmitted(false);
            setForm({ contact_name: userName || '', contact_email: userEmail || '', callback_number: '', issue_subject: '', issue_description: '', priority: 'Medium' });
        }, 300);
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium shadow-lg shadow-cyan-900/30 transition-all hover:scale-105"
                data-testid="floating-help-btn"
                title="IT Support"
            >
                <Headphones className="w-4 h-4" />
                <span className="hidden sm:inline">IT Help</span>
            </button>

            <Dialog open={open} onOpenChange={v => { if (!v) reset(); }}>
                <DialogContent className="bg-[#0f0a1e] border-cyan-500/20 text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Headphones className="w-5 h-5 text-cyan-400" />
                            IT Support Request
                        </DialogTitle>
                        <DialogDescription className="text-violet-200/60">
                            Submit a help request and our IT team will contact you shortly.
                        </DialogDescription>
                    </DialogHeader>

                    {!submitted ? (
                        <form onSubmit={handleSubmit} className="space-y-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div className="space-y-1">
                                    <Label className="text-violet-200/70 text-xs">Your Name *</Label>
                                    <Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })}
                                        className="bg-violet-950/40 border-violet-500/25 text-white placeholder:text-violet-400/40"
                                        placeholder="Full name" data-testid="help-contact-name" />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-violet-200/70 text-xs">Email *</Label>
                                    <Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                                        className="bg-violet-950/40 border-violet-500/25 text-white placeholder:text-violet-400/40"
                                        placeholder="your@email.com" data-testid="help-contact-email" />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-violet-200/70 text-xs">Callback Number *</Label>
                                <Input value={form.callback_number} onChange={e => setForm({ ...form, callback_number: e.target.value })}
                                    className="bg-violet-950/40 border-violet-500/25 text-white placeholder:text-violet-400/40"
                                    placeholder="(555) 123-4567" data-testid="help-callback" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-violet-200/70 text-xs">Issue Subject *</Label>
                                <Input value={form.issue_subject} onChange={e => setForm({ ...form, issue_subject: e.target.value })}
                                    className="bg-violet-950/40 border-violet-500/25 text-white placeholder:text-violet-400/40"
                                    placeholder="Brief summary of the issue" data-testid="help-subject" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-violet-200/70 text-xs">Describe the Issue *</Label>
                                <textarea value={form.issue_description} onChange={e => setForm({ ...form, issue_description: e.target.value })}
                                    rows={4}
                                    className="w-full rounded-lg bg-violet-950/40 border border-violet-500/25 text-white p-3 text-sm placeholder:text-violet-400/40"
                                    placeholder="Please describe the issue in detail..."
                                    data-testid="help-description" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-violet-200/70 text-xs">Urgency Level</Label>
                                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                                    <SelectTrigger className="bg-violet-950/40 border-violet-500/25 text-white" data-testid="help-priority">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0f0a1e] border-violet-500/25">
                                        <SelectItem value="Low" className="text-white">Low — General question</SelectItem>
                                        <SelectItem value="Medium" className="text-white">Medium — Impacts workflow</SelectItem>
                                        <SelectItem value="High" className="text-white">High — Major disruption</SelectItem>
                                        <SelectItem value="Critical" className="text-white">Critical — System down</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={loading} className="bg-cyan-600 hover:bg-cyan-500 text-white w-full" data-testid="help-submit-btn">
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                    Submit Request
                                </Button>
                            </DialogFooter>
                        </form>
                    ) : (
                        <div className="text-center py-6 space-y-3" data-testid="help-success">
                            <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto">
                                <CheckCircle className="w-7 h-7 text-emerald-400" />
                            </div>
                            <h3 className="text-white font-medium">Request Submitted!</h3>
                            <p className="text-sm text-violet-200/70">Our IT team has been notified and will reach out to you shortly at the contact information provided.</p>
                            <Button onClick={reset} variant="outline" className="border-violet-500/30 text-violet-200 hover:bg-violet-500/10 mt-2" data-testid="help-close-btn">Close</Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
